import { useEffect, useState } from 'react';
import { download } from './exporters';

// product photos & data sheets of manufacturer catalogues, stored offline in Cache Storage
const CACHE = 'giss-mfr-assets';
const ok = () => typeof caches !== 'undefined';

export const assetsOf = (doc) => [...new Set((doc?.items || []).flatMap((x) => [x.img, x.pdf]).filter((u) => /^https?:\/\//i.test(u || '')))];

export async function assetStatus(doc) {
  if (!ok()) return { have: 0, total: 0 };
  const c = await caches.open(CACHE), urls = assetsOf(doc);
  const hits = await Promise.all(urls.map((u) => c.match(u)));
  return { have: hits.filter(Boolean).length, total: urls.length };
}

export async function cacheAssets(doc, onProgress) {
  if (!ok()) return { have: 0, total: 0, fail: 0 };
  try { await navigator.storage?.persist?.(); } catch { /* optional */ }
  const c = await caches.open(CACHE), urls = assetsOf(doc);
  let done = 0, fail = 0;
  const queue = [...urls];
  const work = async () => {
    for (let u = queue.shift(); u; u = queue.shift()) {
      try {
        if (!(await c.match(u))) {
          const r = await fetch(u, { mode: 'cors' });
          if (!r.ok) throw new Error(r.status);
          await c.put(u, r);
        }
      } catch { fail += 1; }
      done += 1;
      onProgress?.(done, urls.length);
    }
  };
  await Promise.all([work(), work(), work(), work()]);
  return { have: done - fail, total: urls.length, fail };
}

export async function removeAssets(doc) {
  if (!ok()) return;
  const c = await caches.open(CACHE);
  await Promise.all(assetsOf(doc).map((u) => c.delete(u)));
}

const blobUrl = async (u) => {
  if (!ok() || !u) return null;
  const r = await (await caches.open(CACHE)).match(u);
  return r ? URL.createObjectURL(await r.blob()) : null;
};

// image source: offline copy when stored, otherwise the online URL
export function useAssetUrl(u) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null, live = true;
    blobUrl(u).then((b) => { url = b; if (live) setSrc(b); });
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [u]);
  return src || u;
}

export function MfrImg({ src, ...rest }) {
  const s = useAssetUrl(/^https?:\/\//i.test(src || '') ? src : '');
  return s ? <img src={s} alt="" loading="lazy" {...rest} /> : null;
}

// open a data sheet: offline copy first (installed app: save/share the PDF file)
export async function openAsset(u, e) {
  e?.preventDefault?.();
  e?.stopPropagation?.();
  const r = ok() ? await (await caches.open(CACHE)).match(u) : null;
  if (!r) { window.open(u, '_blank', 'noopener'); return; }
  const blob = await r.blob();
  if (window.matchMedia?.('(display-mode: standalone)').matches) { download(blob, decodeURIComponent(u.split('/').pop().split('?')[0]) || 'datenblatt.pdf'); return; }
  window.open(URL.createObjectURL(blob), '_blank');
}
